import { useCallback, useState } from "react";

/**
 * Supported paste formats for tabular/rich content
 */
export type PasteFormat = "markdown" | "html" | "plain" | "csv";

export interface PasteFormatOption {
  format: PasteFormat;
  label: string;
  description: string;
  preview: string;
  recommended?: boolean;
}

export interface RichPasteData {
  /** Original plain text from clipboard */
  plainText: string;
  /** HTML content if available */
  html: string | null;
  /** Whether the content appears to be tabular (from Excel, etc.) */
  isTabular: boolean;
  /** Available format options */
  formatOptions: PasteFormatOption[];
}

/** Maximum paste size in bytes (2MB) - prevents browser freeze on huge pastes */
const MAX_PASTE_SIZE_BYTES = 2 * 1024 * 1024;

interface UseRichPasteOptions {
  /** Default format to use when tabular content is detected */
  defaultFormat?: PasteFormat;
  /** Whether to auto-convert without showing picker (uses defaultFormat) */
  autoConvert?: boolean;
  /** Callback when content should be inserted */
  onInsert: (text: string) => void;
  /** Callback when paste is rejected (e.g., too large) */
  onError?: (message: string) => void;
}

interface UseRichPasteReturn {
  /** Handle paste event - attach to input element */
  handlePaste: (event: React.ClipboardEvent) => void;
  /** Currently pending paste data (when showing picker) */
  pendingPaste: RichPasteData | null;
  /** Select a format and insert the content */
  selectFormat: (format: PasteFormat) => void;
  /** Cancel the pending paste */
  cancelPaste: () => void;
  /** Whether the format picker should be shown */
  showPicker: boolean;
}

/**
 * Detects if HTML content contains a table structure
 */
function containsTable(html: string): boolean {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.querySelector("table") !== null;
}

/**
 * Detects if plain text appears to be tab-separated (from spreadsheet)
 */
function isTabSeparated(text: string): boolean {
  const lines = text.split("\n").filter((line) => line.trim());
  if (lines.length < 1) return false;

  // Check if multiple lines have tabs
  const linesWithTabs = lines.filter((line) => line.includes("\t"));
  return linesWithTabs.length >= 1 && linesWithTabs.length === lines.length;
}

/**
 * Extract body content from full HTML document
 * (Excel pastes full HTML with DOCTYPE, html, head, body wrappers)
 * Keeps all content including text before/after tables
 */
export function extractBodyContent(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body;

  if (!body) return html;

  // Return the body's innerHTML, stripped of document wrapper
  return body.innerHTML;
}

/**
 * Convert HTML table to Markdown table format
 */
export function htmlTableToMarkdown(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const table = doc.querySelector("table");

  if (!table) return "";

  const rows: string[][] = [];
  const tableRows = table.querySelectorAll("tr");

  tableRows.forEach((tr) => {
    const cells: string[] = [];
    tr.querySelectorAll("th, td").forEach((cell) => {
      // Get text content, trim, and escape pipes
      let text = (cell.textContent || "").trim();
      text = text.replace(/\|/g, "\\|");
      text = text.replace(/\n/g, " "); // Replace newlines with spaces
      cells.push(text);
    });
    if (cells.length > 0) {
      rows.push(cells);
    }
  });

  if (rows.length === 0) return "";

  // Determine max columns
  const maxCols = Math.max(...rows.map((r) => r.length));

  // Normalize rows to have same number of columns
  const normalizedRows = rows.map((row) => {
    while (row.length < maxCols) {
      row.push("");
    }
    return row;
  });

  // Calculate column widths for better formatting
  const colWidths = Array(maxCols).fill(3);
  normalizedRows.forEach((row) => {
    row.forEach((cell, i) => {
      colWidths[i] = Math.max(colWidths[i], cell.length);
    });
  });

  // Build markdown table
  const lines: string[] = [];

  // Header row (first row)
  const headerRow = normalizedRows[0];
  lines.push(
    "| " +
      headerRow.map((cell, i) => cell.padEnd(colWidths[i])).join(" | ") +
      " |",
  );

  // Separator row
  lines.push("| " + colWidths.map((w) => "-".repeat(w)).join(" | ") + " |");

  // Data rows
  for (let i = 1; i < normalizedRows.length; i++) {
    const row = normalizedRows[i];
    lines.push(
      "| " + row.map((cell, j) => cell.padEnd(colWidths[j])).join(" | ") + " |",
    );
  }

  return lines.join("\n");
}

/**
 * Convert tab-separated text to Markdown table
 */
export function tsvToMarkdown(text: string): string {
  const lines = text.split("\n").filter((line) => line.trim());
  if (lines.length === 0) return text;

  const rows = lines.map((line) =>
    line.split("\t").map((cell) => {
      let text = cell.trim();
      text = text.replace(/\|/g, "\\|");
      return text;
    }),
  );

  const maxCols = Math.max(...rows.map((r) => r.length));

  // Normalize rows
  const normalizedRows = rows.map((row) => {
    while (row.length < maxCols) {
      row.push("");
    }
    return row;
  });

  // Calculate column widths
  const colWidths = Array(maxCols).fill(3);
  normalizedRows.forEach((row) => {
    row.forEach((cell, i) => {
      colWidths[i] = Math.max(colWidths[i], cell.length);
    });
  });

  // Build markdown table
  const mdLines: string[] = [];

  // Header row
  mdLines.push(
    "| " +
      normalizedRows[0]
        .map((cell, i) => cell.padEnd(colWidths[i]))
        .join(" | ") +
      " |",
  );

  // Separator
  mdLines.push("| " + colWidths.map((w) => "-".repeat(w)).join(" | ") + " |");

  // Data rows
  for (let i = 1; i < normalizedRows.length; i++) {
    mdLines.push(
      "| " +
        normalizedRows[i]
          .map((cell, j) => cell.padEnd(colWidths[j]))
          .join(" | ") +
        " |",
    );
  }

  return mdLines.join("\n");
}

/**
 * Convert tab-separated text to CSV
 */
export function tsvToCsv(text: string): string {
  const lines = text.split("\n").filter((line) => line.trim());
  return lines
    .map((line) =>
      line
        .split("\t")
        .map((cell) => {
          const trimmed = cell.trim();
          // Quote cells that contain commas, quotes, or newlines
          if (
            trimmed.includes(",") ||
            trimmed.includes('"') ||
            trimmed.includes("\n")
          ) {
            return `"${trimmed.replace(/"/g, '""')}"`;
          }
          return trimmed;
        })
        .join(","),
    )
    .join("\n");
}

/**
 * Hook for handling rich paste (tables, formatted content) in chat input.
 * Detects tabular content from Excel/spreadsheets and offers format conversion options.
 */
export function useRichPaste(options: UseRichPasteOptions): UseRichPasteReturn {
  const {
    defaultFormat = "markdown",
    autoConvert = false,
    onInsert,
    onError,
  } = options;

  const [pendingPaste, setPendingPaste] = useState<RichPasteData | null>(null);

  const convertToFormat = useCallback(
    (data: RichPasteData, format: PasteFormat): string => {
      switch (format) {
        case "markdown":
          if (data.html && containsTable(data.html)) {
            return htmlTableToMarkdown(data.html);
          }
          if (isTabSeparated(data.plainText)) {
            return tsvToMarkdown(data.plainText);
          }
          return data.plainText;

        case "html":
          // Extract body content, strip DOCTYPE/html/head wrappers
          if (data.html) {
            return extractBodyContent(data.html);
          }
          return data.plainText;

        case "csv":
          if (isTabSeparated(data.plainText)) {
            return tsvToCsv(data.plainText);
          }
          return data.plainText;

        case "plain":
        default:
          return data.plainText;
      }
    },
    [],
  );

  const generatePreview = useCallback(
    (data: RichPasteData, format: PasteFormat): string => {
      const converted = convertToFormat(data, format);
      // Return first 100 chars as preview
      if (converted.length > 100) {
        return converted.substring(0, 100) + "...";
      }
      return converted;
    },
    [convertToFormat],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const plainText = clipboardData.getData("text/plain");
      const html = clipboardData.getData("text/html") || null;

      // Check paste size to prevent browser freeze on huge content
      const totalSize = (plainText?.length || 0) + (html?.length || 0);
      if (totalSize > MAX_PASTE_SIZE_BYTES) {
        event.preventDefault();
        const sizeMB = (totalSize / (1024 * 1024)).toFixed(1);
        onError?.(
          `Content too large to paste (${sizeMB}MB). Maximum size is 2MB.`,
        );
        return;
      }

      // Check if this is tabular content
      const hasHtmlTable = html && containsTable(html);
      const hasTabSeparated = isTabSeparated(plainText);
      const isTabular = hasHtmlTable || hasTabSeparated;

      // If not tabular content, let default paste behavior handle it
      if (!isTabular) {
        return;
      }

      // Prevent default paste - we'll handle it
      event.preventDefault();

      const pasteData: RichPasteData = {
        plainText,
        html,
        isTabular,
        formatOptions: [
          {
            format: "html",
            label: "Keep Table Formatting",
            description:
              "Best for tables - preserves rows, columns & structure",
            preview: "",
            recommended: true,
          },
          {
            format: "markdown",
            label: "Markdown Table",
            description: "Simple text table format",
            preview: "",
          },
          {
            format: "plain",
            label: "Plain Text",
            description: "Just the text, no formatting",
            preview: "",
          },
        ],
      };

      // Generate previews for each format
      pasteData.formatOptions = pasteData.formatOptions.map((opt) => ({
        ...opt,
        preview: generatePreview(pasteData, opt.format),
      }));

      if (autoConvert) {
        // Auto-convert and insert without showing picker
        const converted = convertToFormat(pasteData, defaultFormat);
        onInsert(converted);
      } else {
        // Show picker for user to choose format
        setPendingPaste(pasteData);
      }
    },
    [
      autoConvert,
      defaultFormat,
      convertToFormat,
      generatePreview,
      onInsert,
      onError,
    ],
  );

  const selectFormat = useCallback(
    (format: PasteFormat) => {
      if (pendingPaste) {
        const converted = convertToFormat(pendingPaste, format);
        onInsert(converted);
        setPendingPaste(null);
      }
    },
    [pendingPaste, convertToFormat, onInsert],
  );

  const cancelPaste = useCallback(() => {
    setPendingPaste(null);
  }, []);

  return {
    handlePaste,
    pendingPaste,
    selectFormat,
    cancelPaste,
    showPicker: pendingPaste !== null,
  };
}

export default useRichPaste;
